import { Client } from '@microsoft/microsoft-graph-client';
import { IAppContext } from 'App/AppContext';
import { globalMaxSharePointListSize } from 'globalConstants';
import { GraphScopeId } from 'services/Auth/authConfig';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { stripOData } from 'utils/object';
import { getURLBase } from 'utils/url';
import { execDelete, execGet, execPatch, execPost } from './graphServiceCore';
import { sanitiseBannerWebPart } from './graphServicePageCleaner';
import {
  HorizontalSectionLayoutType,
  IList,
  IListItem,
  INewSitePage,
  ISitePage,
  IStandardWebPart,
  ITextWebPart,
  IUpdateSitePage,
  IWebPart,
  IWebPartType,
} from './SharepointInterfaces';

export enum webPartTypes {
  image = 'd1d91016-032f-456d-98a4-721247c305e8',
  banner = 'cbe7b0a9-3504-44dd-a3a3-0e5cacd07788',
}

//
// Sharepoint Pages
//
export const graphValidateSitePageItem = async (client: Client, siteId: string, pageId: string): Promise<boolean> => {
  try {
    const path = `sites/${siteId}/pages/${pageId}`;
    logger.debug('graphValidateSitePageItem', path);
    await execGet(client.api(path));

    return true;
  } catch (err) {
    logger.debug('graphValidateSitePageItem', err);

    return false;
  }
};

const graphGetValidWebParts = (page: ISitePage): IWebPart[] => {
  //Old style web parts
  if (page.canvasLayout?.webParts) {
    const validWebParts: IWebPart[] = [];
    for (const wp of page.canvasLayout.webParts) {
      const validWebPart = getWebPartIncludingType(wp);
      if (validWebPart) {
        validWebParts.push(validWebPart);
      }
    }

    return validWebParts;
  } else {
    return [];
  }
};

export const GetPageBannerWebPart = (page: ISitePage): IStandardWebPart | undefined => {
  if (!page) return undefined;

  try {
    if (page.canvasLayout && page.canvasLayout.horizontalSections) {
      for (let horIdx = 0; horIdx < page.canvasLayout.horizontalSections?.length; horIdx++) {
        const horSection = page.canvasLayout.horizontalSections[horIdx];
        for (let colIdx = 0; colIdx < horSection.columns.length; colIdx++) {
          const col = horSection.columns[colIdx];
          for (let wpIdx = 0; wpIdx < col.webparts.length; wpIdx++) {
            const wp = col.webparts[wpIdx];
            if (wp['@odata.type'] === '#microsoft.graph.standardWebPart') {
              const bannerWp = wp as IStandardWebPart;
              if (bannerWp.webPartType === webPartTypes.banner) {
                // Found the banner web part
                return bannerWp;
              }
            }
          }
        }
      }
    }

    return undefined;
  } catch (err) {
    logger.debug('GetPageBannerWebPart', err);

    return undefined;
  }
};

export const getWebPartIncludingType = (webPart: IWebPart): IWebPart | undefined => {
  try {
    switch (webPart['@odata.type']) {
      case '#microsoft.graph.textWebPart':
        webPart.internalType = IWebPartType.text;

        return webPart;
      case '#microsoft.graph.standardWebPart':
        const standardWebPart = webPart as IStandardWebPart;
        switch (standardWebPart.webPartType) {
          case webPartTypes.image:
            const images = standardWebPart.data?.serverProcessedContent?.imageSources;
            if (images && images.length > 0 && images[0].key === 'imageSource') {
              webPart.internalType = IWebPartType.image;

              return webPart;
            }
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }

    return undefined;
  } catch (err) {
    logger.debug('getWebPartIncludingType', err);

    return undefined;
  }
};

export const getValidPageContent = (page: ISitePage | undefined): ISitePage | undefined => {
  if (!page) return undefined;

  try {
    //Remove invalid web parts, empty columns, and empty horizontal sections
    if (page.canvasLayout && page.canvasLayout.horizontalSections) {
      for (let horIdx = 0; horIdx < page.canvasLayout.horizontalSections?.length; horIdx++) {
        const horSection = page.canvasLayout.horizontalSections[horIdx];
        for (let colIdx = 0; colIdx < horSection.columns.length; colIdx++) {
          const col = horSection.columns[colIdx];
          for (let wpIdx = 0; wpIdx < col.webparts.length; wpIdx++) {
            const wp = col.webparts[wpIdx];
            const validWebPart = getWebPartIncludingType(wp);
            if (!validWebPart) {
              // Remove invalid web part
              col.webparts.splice(wpIdx, 1);
            }
          }
          if (col.webparts.length === 0) {
            // Remove empty column
            horSection.columns.splice(colIdx, 1);
            colIdx--; // Adjust index after removal
          }
        }
        if (horSection.columns.length === 0) {
          // Remove empty horizontal section
          page.canvasLayout.horizontalSections.splice(horIdx, 1);
          horIdx--; // Adjust index after removal
        }
      }
    }

    //Remove the vertical section if it exists and is empty
    if (page.canvasLayout && page.canvasLayout.verticalSection) {
      const verticalSection = page.canvasLayout.verticalSection;
      for (let wpIdx = 0; wpIdx < verticalSection.webparts?.length; wpIdx++) {
        const wp = verticalSection.webparts[wpIdx];
        const validWebPart = getWebPartIncludingType(wp);
        if (!validWebPart) {
          // Remove invalid web part
          verticalSection.webparts.splice(wpIdx, 1);
        }
      }
      if (verticalSection.webparts.length === 0) {
        // Remove empty vertical section
        delete page.canvasLayout.verticalSection;
      }
    }

    return page;
  } catch (err) {
    logger.debug('getValidPageContent', err);

    return undefined;
  }
};

const graphGetCanvasValidWebParts = (page: ISitePage | undefined): IWebPart[] => {
  //New style web parts
  const validWebparts: IWebPart[] = [];

  if (!page) {
    logger.debug('Page is undefined');

    return validWebparts;
  }

  try {
    if (page.canvasLayout && page.canvasLayout.horizontalSections) {
      for (let horIdx = 0; horIdx < page.canvasLayout.horizontalSections?.length; horIdx++) {
        const horSection = page.canvasLayout.horizontalSections[horIdx];
        for (let colIdx = 0; colIdx < horSection.columns.length; colIdx++) {
          const col = horSection.columns[colIdx];
          for (let wpIdx = 0; wpIdx < col.webparts.length; wpIdx++) {
            const wp = col.webparts[wpIdx];
            const validWebPart = getWebPartIncludingType(wp);
            if (validWebPart) {
              validWebparts.push(validWebPart);
            }
          }
        }
      }
    }
    if (page.canvasLayout && page.canvasLayout.verticalSection) {
      for (let wpIdx = 0; wpIdx < page.canvasLayout.verticalSection.webparts.length; wpIdx++) {
        const wp = page.canvasLayout.verticalSection.webparts[wpIdx];
        const validWebPart = getWebPartIncludingType(wp);
        if (validWebPart) {
          validWebparts.push(validWebPart);
        }
      }
    }
  } catch {
    //ignore
  }

  if (validWebparts.length === 0) {
    logger.debug('No valid webparts found in page', page);
  }

  return validWebparts;
};

export const graphGetSitePage = async (
  client: Client,
  siteId: string,
  pageId: string,
  expandWebParts: boolean,
): Promise<ISitePage> => {
  try {
    let query = `sites/${siteId}/pages/${pageId}/microsoft.graph.sitePage`;
    if (expandWebParts) query += '?expand=canvasLayout';
    logger.debug('graphGetSitePage', query);
    const page = await execGet(client.api(query));
    logger.debug('graphGetSitePage result', page);

    return page;
  } catch (err) {
    logger.debug('graphGetSitePage', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetSitePageList = async (client: Client, siteId: string): Promise<IList | undefined> => {
  try {
    const lists = await execGet(client.api(`sites/${siteId}/lists`).select('id,name,system'));
    if (lists && lists.value) {
      const pageList: IList = lists.value.find((l: IList) => l.name?.toLocaleLowerCase() === 'sitepages');
      if (pageList) {
        return pageList;
      }
    }

    return undefined;
  } catch (err) {
    logger.debug('graphGetSitePage', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetSitePageListItem = async (
  client: Client,
  siteId: string,
  listId: string,
  webUrl: string,
): Promise<IListItem | undefined> => {
  try {
    const items = await execGet(client.api(`sites/${siteId}/lists/${listId}/items`));
    const item: IListItem = items.value.find(
      (i: IListItem) => i.webUrl?.toLocaleLowerCase() === webUrl.toLocaleLowerCase(),
    );
    if (item) {
      return item;
    }

    return undefined;
  } catch (err) {
    logger.debug('graphGetSitePage', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetPagesForSite = async (
  client: Client,
  siteId: string,
  expandWebParts: boolean,
  selectFields?: string[],
): Promise<ISitePage[]> => {
  try {
    let query = `sites/${siteId}/pages/microsoft.graph.sitePage`;
    if (expandWebParts) query += '?expand=canvasLayout';
    if (selectFields && selectFields.length > 0) {
      const selectStr = selectFields.join(',');
      query += expandWebParts ? `&select=${selectStr}` : `?select=${selectStr}`;
    }
    logger.debug('graphGetPagesForSite', query);
    let rawPages = await execGet(client.api(query));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let rawItems: any[] = [];

    if (rawPages && rawPages.value) {
      let nextLink = rawPages['@odata.nextLink'];
      rawItems = [...rawPages.value];

      while (nextLink) {
        logger.debug('graphGetPagesForSite', nextLink);
        rawPages = await execGet(client.api(nextLink));
        if (!rawPages) break;
        if (rawPages.value) {
          rawItems.push(...rawPages.value);
        }
        nextLink = rawPages['@odata.nextLink'];
        if (rawItems.length > globalMaxSharePointListSize) break;
      }
    }

    const pages: ISitePage[] = [];

    for (let idx = 0; idx < rawItems.length; idx++) {
      const rawPage = rawItems[idx];
      const page: ISitePage = rawPage;
      pages.push(page);
    }

    return pages;
  } catch (err) {
    logger.debug('graphGetPagesForSite', err);
    throw AppError.fromGraphError(err);
  }
};

export const getWebParts = (page: ISitePage): IWebPart[] => {
  //handle old and new data model for webparts
  const newWebParts: IWebPart[] = [];

  if (page.canvasLayout?.webParts && page.canvasLayout?.webParts.length > 0) {
    //old style
    const oldWebParts = graphGetValidWebParts(page);
    newWebParts.push(...oldWebParts);
  } else {
    // new style
    const webParts = graphGetCanvasValidWebParts(page);
    newWebParts.push(...webParts);
  }

  return newWebParts;
};

export const getTextWebParts = (page: ISitePage): ITextWebPart[] => {
  //handle old and new data model for webparts
  const newWebParts: ITextWebPart[] = [];

  if (page.canvasLayout?.webParts && page.canvasLayout?.webParts.length > 0) {
    //old style
    const oldWebParts = graphGetValidWebParts(page);
    newWebParts.push(...(oldWebParts.filter((w) => w.internalType === IWebPartType.text) as ITextWebPart[]));
  } else {
    // new style
    const webParts = graphGetCanvasValidWebParts(page);
    newWebParts.push(...(webParts.filter((w) => w.internalType === IWebPartType.text) as ITextWebPart[]));
  }

  return newWebParts;
};

export const graphCreateSitePage = async (client: Client, siteId: string, page: ISitePage): Promise<ISitePage> => {
  try {
    const newWebParts = getTextWebParts(page);

    if (!page.title) throw new AppError('Page must have a title');

    const newPage: INewSitePage = {
      '@odata.type': '#microsoft.graph.sitePage',
      name: page.name,
      title: page.title,
      pageLayout: 'article',
      canvasLayout: {
        horizontalSections: [
          {
            id: '1',
            layout: HorizontalSectionLayoutType.oneColumn,
            columns: [
              {
                id: '1',
                webparts: newWebParts,
              },
            ],
          },
        ],
      },
    };

    logger.debug('graphCreateSitePage', newPage);
    const item = await execPost(client.api(`sites/${siteId}/pages`), newPage);

    return item;
  } catch (err) {
    logger.debug('graphCreateSitePage', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphPublishSitePage = async (client: Client, siteId: string, pageId: string): Promise<void> => {
  try {
    const path = `sites/${siteId}/pages/${pageId}/microsoft.graph.sitePage/publish`;
    logger.debug('graphPublishSitePage', path);
    await execPost(client.api(path), null);
  } catch (err) {
    logger.debug('graphPublishSitePage', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphDeleteSitePage = async (client: Client, siteId: string, pageId: string): Promise<void> => {
  try {
    const path = `sites/${siteId}/pages/${pageId}`;
    logger.debug('graphDeleteSitePage', path);
    await execDelete(client.api(path));
  } catch (err) {
    logger.debug('graphDeleteSitePage', err);
    const appErr = AppError.fromGraphError(err);
    if (appErr.code?.toLowerCase() === 'itemNotFound') {
      return; //oke
    } else {
      throw appErr;
    }
  }
};

export const graphUpdateSitePageCanvas = async (
  client: Client,
  siteId: string,
  pageId: string | undefined,
  page: ISitePage,
): Promise<ISitePage> => {
  try {
    //
    // Using this API should be avoided if possible, as it replaces the entire canvasLayout
    // structure. Prefer updating individual web parts instead (graphUpdateSitePageTextWebParts).
    //
    if (pageId === undefined) throw new Error('graphUpdateSitePageCanvas: pageId is undefined');

    const newPage: IUpdateSitePage = {
      '@odata.type': '#microsoft.graph.sitePage',
      canvasLayout: stripOData(page.canvasLayout),
    };

    // Ensure the canvasLayout is valid
    // Run cleaner function on the web parts
    newPage.canvasLayout?.horizontalSections
      ?.flatMap((s) => s.columns)
      ?.flatMap((c) => c.webparts)
      ?.forEach(sanitiseBannerWebPart);

    // Update
    logger.debug('graphUpdateSitePageCanvas', newPage);
    const rawPage = await execPatch(client.api(`sites/${siteId}/pages/${pageId}/microsoft.graph.sitePage`), newPage);

    return rawPage;
  } catch (err) {
    logger.debug('graphUpdateSitePageCanvas', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphUpdateSitePageTextWebParts = async (
  client: Client,
  siteId: string,
  pageId: string | undefined,
  page: ISitePage,
): Promise<void> => {
  try {
    if (pageId === undefined) throw new Error('graphUpdateSitePageCanvas: pageId is undefined');

    const textPartsToUpdate = getTextWebParts(page).filter((w) => w.updated === true);

    for (let idx = 0; idx < textPartsToUpdate.length; idx++) {
      const webPart = textPartsToUpdate[idx];
      const wpId = webPart.id;

      try {
        logger.debug('graphUpdateSitePageTextWebParts', webPart);
        await execPatch(
          client.api(`sites/${siteId}/pages/${pageId}/microsoft.graph.sitePage/webParts/${wpId}`),
          webPart,
        );
      } catch (err) {
        const appErr = AppError.fromGraphError(err);
        if (appErr.code === 'itemNotFound') {
          logger.debug('Web part not found, skipping update', wpId);
        } else {
          throw appErr;
        }
      }
    }
  } catch (err) {
    logger.debug('graphUpdateSitePageTextWebParts', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetSitePageImage = async (
  appContext: IAppContext,
  page: ISitePage,
  webPart: IStandardWebPart,
): Promise<string | undefined> => {
  try {
    switch (webPart.webPartType) {
      case webPartTypes.banner:
      case webPartTypes.image:
        const imageSrc = graphGetSitePageImageUri(page, webPart);
        if (isSharePointUri(imageSrc) && webPart.data) {
          const siteId = webPart.data.properties?.siteId;
          const listId = webPart.data.properties?.listId;
          const uniqueId = webPart.data.properties?.uniqueId;
          // If the image is a SharePoint URL, we need to fetch it using the Graph API
          if (siteId && listId && uniqueId) {
            return getImageObjUri(appContext, siteId, listId, uniqueId);
          } else {
            logger.debug('Web part data does not contain siteId, listId, or uniqueId', page);

            return undefined;
          }
        } else {
          return imageSrc; // Return the URL directly if it's not a SharePoint URL
        }
      default:
        break;
    }

    return undefined;
  } catch (err) {
    logger.debug('graphGetSitePageImage', err);
    throw AppError.fromGraphError(err);
  }
};

export const graphGetSitePageImageUri = (pageContent: ISitePage, webPart: IStandardWebPart): string | undefined => {
  if (
    !webPart.data ||
    !webPart.data.serverProcessedContent ||
    !webPart.data.serverProcessedContent.imageSources ||
    webPart.data.serverProcessedContent.imageSources.length === 0
  ) {
    logger.debug('Image part does not have valid data or image sources', webPart);

    return undefined;
  }

  /* If the image is cropped, SharePoint stores a generated thumbnail
    at properties.imageShapeData.savedImageShape.serverRelativeUrl.
    Prefer that URL because it reflects the user’s crop & aspect‑ratio. */
  const shape = webPart.data.properties?.imageShapeData;
  let imageSrc = shape?.savedImageShape?.serverRelativeUrl;
  if (!imageSrc) {
    imageSrc = webPart.data.serverProcessedContent.imageSources[0]?.value;
  }

  if (imageSrc) {
    // If the image URL is relative, we need to prepend the SharePoint base URL
    if (imageSrc.startsWith('/') && pageContent?.webUrl) {
      imageSrc = `${getURLBase(pageContent.webUrl)}${imageSrc}`;
    }
  }

  return imageSrc;
};

const isSharePointUri = (uri: string | undefined): boolean => {
  if (uri?.includes('sharepoint.com') || uri?.startsWith('/sites/')) {
    return true;
  }

  return false;
};

const getSiteUrlFromPageUrl = (pageWebUrl: string): string | undefined => {
  try {
    const url = new URL(pageWebUrl);
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts.length >= 2 && (parts[0] === 'sites' || parts[0] === 'teams')) {
      return `${url.origin}/${parts[0]}/${parts[1]}`;
    }

    return url.origin;
  } catch {
    return undefined;
  }
};

// Cache list lookups per siteId+libraryName to avoid repeated calls for pages with multiple images
const inlineImageListCache = new Map<string, string>();

export const graphGetInlineImageUrl = async (
  appContext: IAppContext,
  siteId: string,
  pageWebUrl: string,
  imageSrc: string,
  listId?: string,
): Promise<string | undefined> => {
  try {
    // Resolve relative URL to absolute
    let absoluteUrl = imageSrc;
    if (imageSrc.startsWith('/')) {
      const base = getURLBase(pageWebUrl);
      if (base) absoluteUrl = base + imageSrc;
    }

    if (!absoluteUrl.includes('sharepoint.com')) return undefined;

    // Strip query params (SharePoint sometimes appends ?csf=1&web=1 etc.)
    const cleanUrl = absoluteUrl.includes('?') ? absoluteUrl.substring(0, absoluteUrl.indexOf('?')) : absoluteUrl;

    const siteUrl = getSiteUrlFromPageUrl(pageWebUrl);
    if (!siteUrl) return undefined;
    if (!cleanUrl.toLowerCase().startsWith(siteUrl.toLowerCase())) return undefined;

    // siteRelPath = e.g. "SiteAssets/SitePages/PageName/image.png"
    const siteRelPath = cleanUrl.substring(siteUrl.length).replace(/^\//, '');
    const slashIdx = siteRelPath.indexOf('/');
    if (slashIdx < 0) return undefined;
    const libraryName = siteRelPath.substring(0, slashIdx);
    const filePathInLibrary = siteRelPath.substring(slashIdx + 1);
    if (!filePathInLibrary) return undefined;

    const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, undefined);

    // Resolve list ID: use the provided listId (from data-listid) or look up by library name.
    // Note: /drives does not include SiteAssets, so we use /lists which covers all libraries.
    let resolvedListId = listId;
    if (!resolvedListId) {
      const cacheKey = `${siteId}:${libraryName}`;
      resolvedListId = inlineImageListCache.get(cacheKey);
      if (!resolvedListId) {
        const listsResult = await execGet(
          graphInterface.client.api(`/sites/${siteId}/lists`).filter(`name eq '${libraryName}'`).select('id'),
        );
        resolvedListId = listsResult.value?.[0]?.id as string | undefined;
        if (resolvedListId) inlineImageListCache.set(cacheKey, resolvedListId);
      }
    }

    if (!resolvedListId) {
      logger.debug(`graphGetInlineImageUrl: list not found for library ${libraryName}`);

      return undefined;
    }

    const request = `/sites/${siteId}/lists/${resolvedListId}/drive/root:/${filePathInLibrary}`;
    const item = await execGet(graphInterface.client.api(request).select('content.downloadUrl'));
    const downloadUrl = item['@microsoft.graph.downloadUrl'];
    if (!downloadUrl) return undefined;

    return downloadUrl;
  } catch (err) {
    logger.debug('graphGetInlineImageUrl', err);
    
    return undefined;
  }
};

const getImageObjUri = async (
  appContext: IAppContext,
  siteId: string,
  listId: string,
  uniqueId: string,
): Promise<string | undefined> => {
  try {
    const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, undefined);
    const request = `/sites/${siteId}/lists/${listId}/items/${uniqueId}/driveItem`;
    const item = await execGet(graphInterface.client.api(request).select('content.downloadUrl'));
    const stream = await fetch(item['@microsoft.graph.downloadUrl']);
    const blob = await stream.blob();
    const uri = URL.createObjectURL(blob);

    return uri;
  } catch (err) {
    logger.debug('getImageObjUri', err);
    const appErr = AppError.fromGraphError(err);
    if (appErr.code?.toLowerCase() === 'itemNotFound') {
      return undefined; //oke
    } else {
      throw appErr;
    }
  }
};
